import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import { AuthPageWrapper, useRandomSlug } from '@web-apps/feature-auth'
import { Box, Flex, LoadingSpinner, Theme } from '@web-apps/ui-shared'

import { NoAccess } from '../../../components'
import { useRegisterUserMutation } from '../../../services/auth'
import { config } from '../../../utils/helpers/config.helpers'
import { getEnvironmentConfigs } from '../../../services/utility'
import { isUserBannedApiError, Localisation } from '@web-apps/utils-shared'
import styled from 'styled-components'

const StyledSuccessText = styled.p`
  font-size: 14px;
  line-height: 24px;
  text-align: center;
  color: ${Theme.Colors.typography.label};
`

export const PostRegistrationPage = ({
  sessionData,
  onZezamUserRegister,
  onCredentialRejected,
}: {
  sessionData: { metapicUserId?: number }
  onZezamUserRegister: () => void
  onCredentialRejected: () => void
}) => {
  const { t } = useTranslation(['auth'])
  const { search } = useLocation()
  const searchParams = useMemo(() => new URLSearchParams(search), [search])

  const predefinedSlug = searchParams.get('slug')

  const { validSlug } = useRandomSlug({
    skip: Boolean(predefinedSlug),
    baseURL: config.apiBaseUrl || '',
  })

  const [
    registerCreator,
    { isSuccess: registrationSuccess, error: registrationError },
  ] = useRegisterUserMutation()
  const hasRegisteredRef = useRef(false)
  const isUserBanned = isUserBannedApiError(registrationError)
  const onError = (error: string) => toast.error(error)

  const importedProfile = searchParams.get('profile')

  useEffect(() => {
    const slug = predefinedSlug || validSlug

    if (!slug || hasRegisteredRef.current) return

    hasRegisteredRef.current = true

    registerCreator({
      slug,
      time_zone:
        Localisation.CURRENT_DEVICE_TIMEZONE || Localisation.DEFAULT_TIMEZONE,
      ...(importedProfile && {
        page_import_id: importedProfile,
      }),
      is_metapic_referral: true,
    })
  }, [sessionData, validSlug, registerCreator, predefinedSlug, importedProfile])

  useEffect(() => {
    if (registrationSuccess) onZezamUserRegister()
  }, [registrationSuccess, onZezamUserRegister])

  useEffect(() => {
    if (!registrationError || isUserBanned) return

    const status = (registrationError as { status?: number })?.status

    if (status === 401) {
      onCredentialRejected()
      return
    }

    onError(t('auth:errors.reload_page'))
  }, [t, registrationError, isUserBanned, onCredentialRejected])

  if (isUserBanned) return <NoAccess />

  return (
    <div>
      <AuthPageWrapper
        homePage={getEnvironmentConfigs().homePage}
        partner={searchParams.get('partner')}
      >
        {importedProfile && (
          <StyledSuccessText>
            {t('auth:register.upload_profile_description')}
          </StyledSuccessText>
        )}
        <Box mb={40} mt={40}>
          <Flex justify="center">
            <LoadingSpinner size={40} color={Theme.Colors.typography.regular} />
          </Flex>
        </Box>
      </AuthPageWrapper>
    </div>
  )
}
